import { useMemo } from 'react';
import { Field, FieldTypes, OptionKey } from '@common-fe/common-fe';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { NO_KEY, REQUIRED_TEXT, YES_KEY } from '@/common/constants';
import { Option } from '@/common/types';
import { useFieldsWithDefaultValues } from '@/utils';

import { ALL_OTHERS_NOT_SPECIFIED } from '../AddServicePaymentModal.constants';
import { MainInfo } from '../CreateProcessingRule.types';

const handleAllOthersSelection = (
  newValue: OptionKey,
  currentValues: OptionKey[],
  unusedServiceIds?: string[]
): OptionKey[] | null => {
  if (newValue !== ALL_OTHERS_NOT_SPECIFIED || !unusedServiceIds?.length) {
    return null;
  }

  const isCurrentlySelected = currentValues.includes(ALL_OTHERS_NOT_SPECIFIED);

  if (isCurrentlySelected) {
    return currentValues.filter(
      (val) => val !== ALL_OTHERS_NOT_SPECIFIED && !unusedServiceIds.includes(String(val))
    );
  }

  const otherSelected = currentValues.filter((val) => !unusedServiceIds.includes(String(val)));
  return Array.from(new Set([ALL_OTHERS_NOT_SPECIFIED, ...otherSelected, ...unusedServiceIds]));
};

const PAYMENT_VALUE_KEY = 'paymentValue';

export const AMOUNT_KEY = 'AMOUNT';
export const PERCENTAGE_KEY = 'PERCENTAGE';

const showOnlyEligibleServicesOptions = [
  {
    label: 'Yes',
    value: YES_KEY,
  },
  {
    label: 'No',
    value: NO_KEY,
  },
];

export default (
  defaultValues?: Partial<MainInfo>,
  serviceTypeOptions?: Option[],
  errors?: Partial<MainInfo>,
  hasLinkedPlans?: boolean,
  unusedServiceIds?: string[]
) => {
  const fields = useMemo<Field[]>(
    () => [
      {
        name: 'showEligibleServices',
        type: FieldTypes.Radio,
        label: 'Show only eligible services',
        showRequireIcon: true,
        validator: VALIDATORS.REQUIRED_STRING,
        options: showOnlyEligibleServicesOptions,
        defaultValue: NO_KEY,
        disabled: !hasLinkedPlans,
      },
      {
        name: 'serviceTypes',
        type: FieldTypes.MultipleDropdown,
        label: 'Service types',
        multiple: true,
        options: serviceTypeOptions,
        placeholder: 'Select options',
        showRequireIcon: true,
        isNarrowLabelMode: true,
        onBeforeChange: (newValue: OptionKey, currentValues: OptionKey[]) =>
          handleAllOthersSelection(newValue, currentValues, unusedServiceIds),
        validator: yup
          .array()
          .test({
            test: (val) => {
              if (!errors?.serviceTypes || !Array.isArray(errors.serviceTypes)) return true;
              return !val?.some((v) => (errors.serviceTypes as string[]).includes(v as string));
            },
            message: 'Configuration for this service type already exists',
          })
          .min(1, REQUIRED_TEXT)
          .required(REQUIRED_TEXT),
      },
      {
        name: 'description',
        type: FieldTypes.Text,
        label: 'Description',
        placeholder: 'Enter description',
      },
      {
        name: PAYMENT_VALUE_KEY,
        type: FieldTypes.Radio,
        label: 'Specify payment value?',
        showRequireIcon: true,
        validator: VALIDATORS.REQUIRED_STRING,
        options: [
          {
            label: 'Yes',
            value: YES_KEY,
          },
          {
            label: 'No',
            value: 'no',
          },
        ],
        subFields: [
          {
            name: 'claimValue',
            parentValue: YES_KEY,
            showRequireIcon: true,
            type: FieldTypes.Currency,
            label: 'Matching claim value',
            placeholder: 'Enter $ amount',
            validator: yup.string().when(PAYMENT_VALUE_KEY, {
              is: (val: string) => val === YES_KEY,
              then: (schema) => VALIDATORS.GET_REQUIRED_SCHEMA(schema),
              otherwise: () => VALIDATORS.STRING,
            }),
          },
        ],
      },
      {
        name: 'applyToDeductible',
        type: FieldTypes.Radio,
        label: 'Apply to deductible?',
        showRequireIcon: true,
        validator: VALIDATORS.REQUIRED_STRING,
        options: [
          {
            label: 'Yes',
            value: YES_KEY,
          },
          {
            label: 'No',
            value: 'no',
          },
        ],
      },
      {
        name: 'paymentType',
        type: FieldTypes.Radio,
        label: 'Payment type',
        showRequireIcon: true,
        validator: VALIDATORS.REQUIRED_STRING,
        options: [
          {
            label: 'Amount',
            value: AMOUNT_KEY,
          },
          {
            label: 'Percentage',
            value: PERCENTAGE_KEY,
          },
        ],
      },
    ],
    [hasLinkedPlans, serviceTypeOptions, unusedServiceIds, errors?.serviceTypes]
  );

  return useFieldsWithDefaultValues(fields, defaultValues);
};
